import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup, FormControl, Validators, AbstractControl } from '@angular/forms';
import { finalize, Subscription } from 'rxjs';
import { AuthService } from '../../../authentication/auth.service';
import { ResetPasswordReqBody } from '../../../authentication/auth.models';
import { SnackbarService } from '../../../services/snackbar.service';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { SnackbarAlert, SnackbarAlertStatus } from '../../../utils';

@Component({
  selector: 'app-reset-password-modal',
  standalone: false,
  templateUrl: './reset-password-modal.component.html',
  styleUrl: './reset-password-modal.component.scss',
})
export class ResetPasswordModalComponent implements OnInit, OnDestroy {
  @Output() closeModalEvent: EventEmitter<void> = new EventEmitter();

  protected changePasswordForm!: FormGroup;
  protected isResettingPassword: boolean = false;
  protected showCurrentPassword: boolean = false;
  protected showNewPassword: boolean = false;
  protected showConfirmPassword: boolean = false;

  private snackbarRef!: MatSnackBarRef<any>;
  private authServiceSubscription!: Subscription;

  constructor(
    private authService: AuthService,
    private snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this.changePasswordForm = new FormGroup(
      {
        currentPassword: new FormControl('', Validators.required),
        newPassword: new FormControl('', Validators.required),
        confirmPassword: new FormControl('', Validators.required),
      },
      { validators: passwordsMatchValidator }
    );
  }

  protected onCancel(): void {
    this.changePasswordForm.reset();
    this.closeModalEvent.emit();
  }

  protected onSubmit(): void {
    const { currentPassword, newPassword } = this.changePasswordForm.value;
    const reqBody: ResetPasswordReqBody = {
      new_password: newPassword,
      current_password: currentPassword,
    };

    this.isResettingPassword = true;
    this.authServiceSubscription = this.authService
      .resetPassword(reqBody)
      .pipe(
        finalize(() => {
          this.isResettingPassword = false;
        })
      )
      .subscribe({
        next: () => {
          this.snackbarRef = this.snackbarService.show('Password updated successfully', SnackbarAlertStatus.SUCCESS);
          this.closeModalEvent.emit();
        },
        error: err => {
          let errorMsg = SnackbarAlert.SOMETHING_WENT_WRONG;
          if (err.error?.current_password) {
            errorMsg = err.error.current_password[0];
          } else if (err.error?.new_password) {
            errorMsg = err.error.new_password[0];
          }

          this.snackbarRef = this.snackbarService.show(errorMsg, SnackbarAlertStatus.ERROR);
        },
      });
  }

  ngOnDestroy(): void {
    this.authServiceSubscription?.unsubscribe();
  }
}

export function passwordsMatchValidator(group: AbstractControl): { [key: string]: boolean } | null {
  const newPassword = group.get('newPassword')?.value;
  const confirmPassword = group.get('confirmPassword')?.value;

  if (!newPassword || !confirmPassword) {
    return null;
  }

  return newPassword === confirmPassword ? null : { passwordMismatch: true };
}
